import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/services/api';
import * as TokenService from '@/network/token';
import { http } from '@/network/http';
import i18n from '@/i18n';
import { healthCheckService } from '@/services/healthCheck.service';
import { useAuthStore, useAccessToken } from '../store/authStore';
import { DEFAULT_AUTHENTICATED_ROUTE } from '@/config/routes';

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setAuth, clearAuth, setUser } = useAuthStore();
  const accessToken = useAccessToken();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  // Fetch user profile + permissions from backend.
  // Always runs when authenticated — permissions are never cached in localStorage,
  // so they must be fetched fresh on every app load / window focus.
  // SKIP during impersonation: /auth/me uses the refresh cookie (admin's session),
  // which would overwrite the impersonated user's data.
  const { isLoading, refetch: refetchUser } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const response = await authApi.getMe();
      const userData = response.data.user;
      setUser(userData);
      if (response.data.permissions) {
        useAuthStore.getState().setPermissions(response.data.permissions);
      }
      // Sync language from server (may have changed on another device),
      // but only if onboarding is complete. New users have DB default 'he'
      // which shouldn't override the current UI language.
      const profileComplete = userData?.firstNameHe && userData?.lastNameHe && userData?.phone;
      const lang = userData?.preferredLanguage;
      if (profileComplete && lang && lang !== i18n.language) {
        i18n.changeLanguage(lang);
      }
      return userData;
    },
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    enabled: !!accessToken && !isImpersonating,
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string; remember?: boolean }) => {
      const response = await authApi.login(credentials);
      return response.data;
    },
    onSuccess: (data) => {
      setAuth(data.user, data.accessToken, data.permissions);
      if (data.refreshToken) TokenService.setRefreshToken(data.refreshToken);
      queryClient.setQueryData(['auth', 'profile', data.accessToken], data.user);
      navigate(data.user?.roleRef?.defaultLandingPage || DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    },
    onError: (error: unknown) => {
      const resp = (error as { response?: { data?: { pendingApproval?: boolean; user?: { firstName?: string } } } })?.response?.data;
      if (resp?.pendingApproval) {
        const name = resp.user?.firstName ? `&name=${encodeURIComponent(resp.user.firstName)}` : '';
        navigate(`/login?reason=pending_approval${name}`, { replace: true });
      }
    },
  });

  const performLogoutCleanup = () => {
    // 1. Stop health monitoring — prevents false "Service Unavailable" during transition
    healthCheckService.stopMonitoring();

    // 2. Cancel in-flight queries to avoid network errors during navigation
    queryClient.cancelQueries();

    // 3. Clear auth state (persisted to localStorage via zustand/persist)
    clearAuth();

    // 4. Navigate to login — SPA navigation, no full page reload.
    navigate('/login', { replace: true });

    // 5. Clear query cache after navigation so unmounted components don't crash.
    setTimeout(() => queryClient.clear(), 100);
  };

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Unregister push token BEFORE logout invalidates the session
      const pushToken = localStorage.getItem('fcm_push_token');
      if (pushToken) {
        await http.delete('/push/unregister', { data: { token: pushToken } }).catch(() => {});
        localStorage.removeItem('fcm_push_token');
      }
      return authApi.logout();
    },
    onSuccess: performLogoutCleanup,
    onError: performLogoutCleanup,
  });

  const sendOtpMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await authApi.sendOtp(email);
      return response.data;
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      const response = await authApi.verifyOtp(email, code);
      return response.data;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; firstName: string; lastName: string; firstNameHe: string; lastNameHe: string; phone: string; verificationToken: string; preferredLanguage?: string }) => {
      const response = await authApi.register(data);
      return response.data;
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword({ email }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword({ token, password }),
  });

  const googleSignInMutation = useMutation({
    mutationFn: async (credential: string) => {
      const response = await authApi.googleSignIn(credential, i18n.language);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.pendingApproval) {
        const name = data.user?.firstName ? `&name=${encodeURIComponent(data.user.firstName)}` : '';
        navigate(`/login?reason=pending_approval${name}`, { replace: true });
        return;
      }
      setAuth(data.user, data.accessToken, data.permissions);
      if (data.refreshToken) TokenService.setRefreshToken(data.refreshToken);
      queryClient.setQueryData(['auth', 'profile', data.accessToken], data.user);
      navigate(data.user?.roleRef?.defaultLandingPage || DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    },
  });

  const appleSignInMutation = useMutation({
    mutationFn: async (data: { identityToken: string; user?: { name?: { firstName?: string; lastName?: string } } }) => {
      const response = await authApi.appleSignIn(data.identityToken, data.user, i18n.language);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.pendingApproval) {
        const name = data.user?.firstName ? `&name=${encodeURIComponent(data.user.firstName)}` : '';
        navigate(`/login?reason=pending_approval${name}`, { replace: true });
        return;
      }
      setAuth(data.user, data.accessToken, data.permissions);
      if (data.refreshToken) TokenService.setRefreshToken(data.refreshToken);
      queryClient.setQueryData(['auth', 'profile', data.accessToken], data.user);
      navigate(data.user?.roleRef?.defaultLandingPage || DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    },
  });

  return {
    user,
    isAuthenticated,
    accessToken,
    isLoading: accessToken && !user ? isLoading : false,
    login: loginMutation.mutate,
    loginLoading: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: logoutMutation.mutate,
    sendOtp: sendOtpMutation.mutateAsync,
    sendOtpLoading: sendOtpMutation.isPending,
    sendOtpError: sendOtpMutation.error,
    verifyOtp: verifyOtpMutation.mutateAsync,
    verifyOtpLoading: verifyOtpMutation.isPending,
    verifyOtpError: verifyOtpMutation.error,
    register: registerMutation.mutateAsync,
    registerLoading: registerMutation.isPending,
    registerError: registerMutation.error,
    registerData: registerMutation.data,
    forgotPassword: forgotPasswordMutation.mutate,
    forgotPasswordLoading: forgotPasswordMutation.isPending,
    resetPassword: resetPasswordMutation.mutate,
    resetPasswordLoading: resetPasswordMutation.isPending,
    googleSignIn: googleSignInMutation.mutate,
    googleSignInLoading: googleSignInMutation.isPending,
    googleSignInError: googleSignInMutation.error,
    googleSignInData: googleSignInMutation.data,
    appleSignIn: appleSignInMutation.mutate,
    appleSignInLoading: appleSignInMutation.isPending,
    appleSignInError: appleSignInMutation.error,
    refetchUser,
  };
}
